import {
  MAX_RICH_TEXT_LENGTH,
  NOTIFICATION_TEMPLATE_TYPE_OPTIONS,
  NOTIFICATION_TEMPLATE_TYPES,
} from '@ibe/constants';
import { z } from 'zod';

/**
 * The Notification Templates module's contracts.
 *
 * Email and SMS share one table each in the database, but the forms treat them
 * as one module because the shape a staff member edits is almost identical:
 * a channel, a type, a name, a body and whether this is the default for that
 * type. Only the subject and sender fields are email-only.
 */

const NAME_MAX = 128;
const CODE_MAX = 128;
const SUBJECT_MAX = 255;
const ADDRESS_MAX = 320;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

export const CHANNEL = {
  EMAIL: 'EMAIL',
  SMS: 'SMS',
} as const;

export type Channel = (typeof CHANNEL)[keyof typeof CHANNEL];

export const CHANNEL_OPTIONS: readonly { value: string; label: string }[] = [
  { value: CHANNEL.EMAIL, label: 'Email' },
  { value: CHANNEL.SMS, label: 'SMS' },
] as const;

/**
 * The template types the admin portal offers.
 *
 * These are the business events the platform sends notifications for, and the
 * same values are enforced at the database by a check constraint on both
 * `notification_email_templates.type` and `notification_sms_templates.type`.
 */
export const TEMPLATE_TYPE_OPTIONS = NOTIFICATION_TEMPLATE_TYPE_OPTIONS;
export const TEMPLATE_TYPES = NOTIFICATION_TEMPLATE_TYPES;

function isTemplateType(value: string): boolean {
  return NOTIFICATION_TEMPLATE_TYPES.some((allowed) => allowed === value);
}

/** One row in the template list. Only `id` is guaranteed. */
export const notificationTemplateRow = z.object({
  id: z.string(),
  propertyId: z.string().nullable().optional(),
  channel: z.string().optional(),
  code: z.string().optional(),
  type: z.string().optional(),
  name: z.string().optional(),
  subject: z.string().nullable().optional(),
  body: z.string().optional(),
  isDefault: z.boolean().optional(),
  isActive: z.boolean().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

export type NotificationTemplateRow = z.infer<typeof notificationTemplateRow>;

/** The full template, as the detail screen and the edit form read it. */
export const notificationTemplateDetail = z.object({
  id: z.string(),
  propertyId: z.string().nullable(),
  channel: z.string(),
  code: z.string(),
  type: z.string(),
  name: z.string(),
  subject: z.string().nullable(),
  body: z.string(),
  variables: z.array(z.string()),
  fromAddress: z.string().nullable(),
  fromName: z.string().nullable(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type NotificationTemplateDetail = z.infer<typeof notificationTemplateDetail>;

/** What the create endpoint returns. */
export const createdNotificationTemplate = z.object({ id: z.string() });

export type CreatedNotificationTemplate = z.infer<typeof createdNotificationTemplate>;

/** The create and edit forms collect the same fields under the same rules. */
export const notificationTemplateFormInput = z
  .object({
    propertyId: z.string(),
    channel: z.string().min(1, 'Choose a channel'),
    code: z
      .string()
      .min(1, 'A template needs a code — it is how the platform picks it')
      .max(CODE_MAX, `Keep the code under ${String(CODE_MAX)} characters`)
      .refine(isTemplateType, { message: 'Choose a recognised template code' }),
    name: z
      .string()
      .trim()
      .min(1, 'A template needs a name')
      .max(NAME_MAX, `Keep the name under ${String(NAME_MAX)} characters`),
    subject: z
      .string()
      .max(SUBJECT_MAX, `Keep the subject under ${String(SUBJECT_MAX)} characters`),
    body: z
      .string()
      .min(1, 'Write the message the guest will receive')
      .max(
        MAX_RICH_TEXT_LENGTH,
        `That message is too long. Keep it under ${String(MAX_RICH_TEXT_LENGTH)} characters.`,
      ),
    fromAddress: z
      .string()
      .max(ADDRESS_MAX, `Keep the address under ${String(ADDRESS_MAX)} characters`),
    fromName: z.string().max(NAME_MAX, `Keep the sender name under ${String(NAME_MAX)} characters`),
    isDefault: z.boolean(),
    isActive: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (values.channel === CHANNEL.EMAIL && values.subject.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['subject'],
        message: 'An email template needs a subject line',
      });
    }

    if (values.fromAddress !== '' && !EMAIL_PATTERN.test(values.fromAddress)) {
      ctx.addIssue({
        code: 'custom',
        path: ['fromAddress'],
        message: 'Enter a valid email address, or leave this blank to use the gateway default',
      });
    }
  });

export type NotificationTemplateFormInput = z.infer<typeof notificationTemplateFormInput>;

export const notificationTemplateTag = z.object({
  id: z.string(),
  channel: z.string(),
  fieldTitle: z.string(),
  fieldName: z.string(),
  replacementTag: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const notificationTemplateTags = z.array(notificationTemplateTag);

export type NotificationTemplateTag = z.infer<typeof notificationTemplateTag>;
export type NotificationTemplateTags = z.infer<typeof notificationTemplateTags>;
